<template>
    <div
        class="bg-white rounded-lg shadow-lg p-5 sticky top-4 lg:flex lg:h-full lg:flex-col"
        :class="$attrs.class"
    >
        <div class="flex-shrink-0">
            <!-- Image hidden in edit mode — PropertyHeroBand renders above the
                 form and is the single source of truth for the cover photo. -->
            <div
                v-if="!hideImage"
                class="h-48 bg-gray-200 rounded-lg mb-4 flex items-center justify-center relative overflow-hidden"
            >
                <img
                    :src="imageSrc"
                    :alt="hasUploadedPhoto ? 'Property image' : 'Default property image'"
                    class="h-full w-full object-cover"
                    @error="handleImageError"
                />
            </div>

            <div v-if="form.address_1" class="space-y-4">
                <h3 class="font-bold text-lg text-gray-900">{{ streetLine }}{{ form.city ? ', ' + form.city : '' }}</h3>

                <div class="flex items-center gap-4 text-sm text-gray-600">
                    <span v-if="form.rent">${{ formatNumber(form.rent) }}/mo</span>
                    <span v-if="form.sq_ft">{{ formatNumber(form.sq_ft) }} sq.ft</span>
                    <span v-if="form.beds && form.baths">{{ form.beds }} bed/{{ form.baths }}bath</span>
                </div>
            </div>
            <div v-else class="text-gray-400 text-sm">
                Property details will appear here
            </div>
        </div>

        <div
            v-if="$slots.footer"
            class="mt-5 space-y-4 border-t border-gray-100 pt-5 lg:mt-auto"
        >
            <slot name="footer" />
        </div>
    </div>
</template>

<script setup>
import { appUrl } from '@/appUrl'
import { computed } from 'vue'

defineOptions({
    inheritAttrs: false,
})

const props = defineProps({
    form: {
        type: Object,
        required: true,
    },
    formatNumber: {
        type: Function,
        required: true,
    },
    // When editing an existing property, pass it in so the summary mirrors
    // the cover photo shown on the property details page (PropertyHeroBand).
    // On create, this stays null and we fall back to the placeholder.
    property: {
        type: Object,
        default: null,
    },
    // Hide the embedded image block (used in edit mode where the
    // PropertyHeroBand above the form is the canonical photo surface).
    hideImage: {
        type: Boolean,
        default: false,
    },
})

const defaultImagePath = '/default-property-image.jpg'

const streetLine = computed(() =>
    [props.form.address_1, props.form.address_2].filter(Boolean).join(' ')
)

const hasUploadedPhoto = computed(() => Boolean(props.property?.photo))

const imageSrc = computed(() => {
    const photo = props.property?.photo
    if (!photo) {
        return defaultImagePath
    }
    return photo.startsWith('http') ? photo : appUrl(`/storage/${photo}`)
})

const handleImageError = (event) => {
    event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23e5e7eb" width="400" height="300"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EProperty Image%3C/text%3E%3C/svg%3E'
}
</script>
